import React, { useState, useMemo } from 'react';
import { FaIdCardAlt, FaUserTie } from 'react-icons/fa';
import { MdCheckCircle, MdCancel, MdDirectionsRun } from 'react-icons/md';
import { BsCircleHalf } from 'react-icons/bs';
import { formatTime } from './shared';

export type FlatRecord = any; // kept for backward-compat with manager view

export interface DailyEmployee {
  id: string;
  fullname: string;
  employeeId: number;
  profilePicture?: string | null;
  designation?: { name: string } | null;
  status: string;
  checkInTime: string | null;
  checkOutTime: string | null;
  workingHours: number | null;
}

interface Props {
  // admin mode: pass dailyData + selectedDate + onDateChange
  dailyData?: DailyEmployee[];
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  // manager mode: pass records (flat records for the month)
  records?: FlatRecord[];
  year?: number;
  month?: number;
  loading: boolean;
  onEmployeeClick: (id: string, name: string, designation: string, profilePicture?: string | null) => void;
}

const STATUS_CONFIG: Record<string, { label: string; icon: React.ReactNode; row: string; badge: string }> = {
  PRESENT:  { label: 'Present',  icon: <MdCheckCircle />,     row: 'bg-green-50/40',  badge: 'bg-green-100 text-green-700 border border-green-200' },
  HALF_DAY: { label: 'Half Day', icon: <BsCircleHalf />,      row: 'bg-purple-50/40', badge: 'bg-purple-100 text-purple-700 border border-purple-200' },
  ON_LEAVE: { label: 'On Leave', icon: <MdDirectionsRun />,   row: 'bg-orange-50/40', badge: 'bg-orange-100 text-orange-700 border border-orange-200' },
  ABSENT:   { label: 'Absent',   icon: <MdCancel />,          row: 'bg-red-50/30',    badge: 'bg-red-100 text-red-600 border border-red-200' },
  WEEKEND:  { label: 'Weekend',  icon: null,                  row: 'bg-gray-50/60',   badge: 'bg-gray-100 text-gray-400 border border-gray-200' },
  UPCOMING: { label: '—',        icon: null,                  row: '',                badge: 'bg-gray-50 text-gray-400 border border-gray-100' },
};

const AttendanceTableView: React.FC<Props> = ({
  dailyData, selectedDate, onDateChange,
  records, year, month,
  loading, onEmployeeClick,
}) => {
  const isAdminMode = !!dailyData;

  // Manager mode: local date filter over flat records
  const today = new Date();
  const toDateStr = (y: number, m: number, d: number) =>
    `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const [localDate, setLocalDate] = useState(() => {
    if (year !== undefined && month !== undefined) {
      return year === today.getFullYear() && month === today.getMonth() + 1
        ? toDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate())
        : toDateStr(year, month, 1);
    }
    return toDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate());
  });

  const managerRows = useMemo(() => {
    if (isAdminMode || !records) return [];
    return records.filter(r => r.date.slice(0, 10) === localDate);
  }, [records, localDate, isAdminMode]);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage]         = useState(10);
  const [search, setSearch]           = useState('');

  const rows = isAdminMode ? (dailyData ?? []) : managerRows;

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r: any) => {
      const name = isAdminMode ? r.fullname : (r.employee?.fullname ?? '');
      const empId = String(isAdminMode ? r.employeeId : (r.employee?.employeeId ?? ''));
      return name.toLowerCase().includes(q) || empId.includes(q);
    });
  }, [rows, search, isAdminMode]);

  const totalPages    = Math.ceil(filtered.length / perPage);
  const paginated     = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const startEntry    = filtered.length === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const endEntry      = Math.min(currentPage * perPage, filtered.length);

  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 4, '...', totalPages];
    if (currentPage >= totalPages - 2) return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  const handleDateChange = (d: string) => {
    setCurrentPage(1);
    if (isAdminMode) onDateChange?.(d);
    else setLocalDate(d);
  };

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl md:text-xl xl:text-3xl text-gray-900 font-semibold">
          Attendance Overview
        </h2>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Search */}
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            placeholder="Search..."
            className="px-4 py-2 text-sm border border-gray-200 bg-white rounded-full focus:outline-none focus:border-gray-300 w-32 sm:w-44 placeholder-gray-400"
          />
          {/* Date picker */}
          <input
            type="date"
            value={selectedDate ?? localDate}
            onChange={e => e.target.value && handleDateChange(e.target.value)}
            className="h-9 border border-gray-200 rounded-xl px-3 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#2D328F]/20 focus:border-[#2D328F]/40 cursor-pointer"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white border-t shadow rounded-xl p-10 text-center text-gray-400 text-sm">
          Loading...
        </div>
      ) : (
        <div className="bg-white border-t shadow rounded-xl overflow-hidden">
          <div className="hidden md:block w-full overflow-x-auto">
            <table className="min-w-[640px] text-center table-fixed w-full">
              <thead>
                <tr className="text-[#D52819] bg-[#FFF8F7] text-[10px] md:text-xs xl:text-sm">
                  <th className="py-3 font-semibold w-[12%]">
                    <div className="flex items-center justify-center px-4 gap-2 whitespace-nowrap">
                      <FaIdCardAlt className="text-xs xl:text-sm" /> ID
                    </div>
                  </th>
                  <th className="py-3 font-semibold text-left">
                    <div className="flex items-center px-4 gap-2 whitespace-nowrap">
                      <FaUserTie className="text-xs md:text-sm" /> Employee Name
                    </div>
                  </th>
                  <th className="py-3 font-semibold w-[14%]">Status</th>
                  <th className="py-3 font-semibold w-[13%]">Check-In</th>
                  <th className="py-3 font-semibold w-[10%]">Hours</th>
                  <th className="py-3 font-semibold w-[13%]">Check-Out</th>
                </tr>
              </thead>
              <tbody className="md:text-xs xl:text-sm">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-gray-400 text-sm">
                      No employees found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((r: any, idx: number) => {
                    const empId      = isAdminMode ? r.id                        : r.employeeId;
                    const name       = isAdminMode ? r.fullname                  : (r.employee?.fullname ?? '—');
                    const desig      = isAdminMode ? (r.designation?.name ?? '') : (r.employee?.designation?.name ?? '');
                    const numericId  = isAdminMode ? r.employeeId                : (r.employee?.employeeId ?? '—');
                    const photo      = isAdminMode ? r.profilePicture            : (r.employee?.profilePicture ?? null);
                    const status     = r.status;
                    const cin        = r.checkInTime;
                    const cout       = r.checkOutTime;
                    const hours      = r.workingHours;
                    const cfg        = STATUS_CONFIG[status] ?? STATUS_CONFIG.UPCOMING;
                    const isWeekend  = status === 'WEEKEND';
                    const isUpcoming = status === 'UPCOMING';

                    return (
                      <tr
                        key={`${empId}-${idx}`}
                        className={`border-b cursor-pointer hover:brightness-95 transition-all ${cfg.row}`}
                        onClick={() => onEmployeeClick(empId, name, desig, photo)}
                      >
                        <td className="py-2.5 px-4 text-gray-700 font-medium">{numericId}</td>
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-2 md:gap-3">
                            {photo ? (
                              <img src={photo} alt={name} className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs uppercase flex-shrink-0">
                                {name[0] ?? '?'}
                              </div>
                            )}
                            <div className="text-left overflow-hidden">
                              <p className="font-medium text-gray-900 truncate">{name}</p>
                              <p className="text-[10px] md:text-xs text-[#999999]">{desig}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-2.5 px-2">
                          {!isWeekend && !isUpcoming && (
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.badge}`}>
                              {cfg.icon && <span className="text-sm">{cfg.icon}</span>}
                              {cfg.label}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-4 font-medium text-gray-700">{formatTime(cin) ?? '—'}</td>
                        <td className="py-2.5 px-4 text-gray-400 whitespace-nowrap text-xs">
                          {hours !== null && hours !== undefined ? `${Number(hours).toFixed(1)}h` : '—'}
                        </td>
                        <td className="py-2.5 px-4 font-medium text-gray-700">{formatTime(cout) ?? '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-3">
            {paginated.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-6">No employees found.</p>
            ) : (
              paginated.map((r: any, idx: number) => {
                const empId  = isAdminMode ? r.id              : r.employeeId;
                const name   = isAdminMode ? r.fullname        : (r.employee?.fullname ?? '—');
                const desig  = isAdminMode ? (r.designation?.name ?? '') : (r.employee?.designation?.name ?? '');
                const numId  = isAdminMode ? r.employeeId      : (r.employee?.employeeId ?? '—');
                const photo  = isAdminMode ? r.profilePicture  : (r.employee?.profilePicture ?? null);
                const status = r.status;
                const cfg    = STATUS_CONFIG[status] ?? STATUS_CONFIG.UPCOMING;

                return (
                  <div
                    key={`${empId}-${idx}`}
                    className={`shadow-sm rounded-xl p-4 border cursor-pointer transition ${cfg.row} hover:brightness-95`}
                    onClick={() => onEmployeeClick(empId, name, desig)}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {photo ? (
                        <img src={photo} alt={name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm uppercase">
                          {name[0] ?? '?'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#999999]">#{numId}</p>
                        <p className="font-semibold text-gray-900 truncate">{name}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${cfg.badge}`}>
                        {cfg.icon && <span>{cfg.icon}</span>}
                        {cfg.label}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-[#999999]">
                      <div>
                        <p className="text-xs">Check-In</p>
                        <p className="font-semibold text-gray-900">{formatTime(r.checkInTime) ?? '—'}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-300">───────</p>
                        <p className="text-xs">{r.workingHours !== null && r.workingHours !== undefined ? `${Number(r.workingHours).toFixed(1)}h` : '—'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs">Check-Out</p>
                        <p className="font-semibold text-gray-900">{formatTime(r.checkOutTime) ?? '—'}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {filtered.length > 0 && (
            <div className="flex flex-wrap items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-t bg-white">
              <p className="text-[10px] md:text-xs xl:text-[13px] text-[#868686] font-medium py-1">
                Showing {startEntry} to {endEntry} of {filtered.length} results
              </p>
              <div className="flex flex-wrap items-center gap-1 md:gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  className="text-[#CCCCCC] text-xs disabled:opacity-50 px-1">Prev</button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`e${i}`} className="px-1 text-[#284867] text-xs">...</span>
                  ) : (
                    <button key={`p${p}`} onClick={() => setCurrentPage(Number(p))}
                      className={`flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-md text-xs md:text-sm ${
                        currentPage === p ? 'bg-[#2F80ED] text-white' : 'border text-[#284867] hover:bg-gray-50'
                      }`}>{p}</button>
                  )
                )}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  className="text-xs md:text-sm text-[#284867] disabled:opacity-50 px-1">Next</button>
                <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="ml-2 px-3 py-1.5 rounded-full text-[#284867] bg-[#F0F0FF] text-xs outline-none">
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceTableView;
